import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Transaction } from '../types';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/dateFormat';
import { useTransactionDirections } from '../hooks/useTransactionDirections';
import { useTransactionTypes } from '../hooks/useTransactionTypes';

interface TransactionsTableProps {
  transactions: Transaction[];
  onEdit?: (transaction: Transaction) => void;
  onDelete?: (id: number) => void;
  onCopy?: (transaction: Transaction) => void;
  showActions?: boolean;
  hideDirection?: boolean;
  hideProject?: boolean;
  hideStatus?: boolean;
  hideDescription?: boolean;
  editOnlyPending?: boolean;
  allowCopy?: boolean;
  highlightPendingRow?: boolean;
  pendingRowColor?: string;
  highlightPendingStatus?: boolean;
  pendingStatusColor?: string;
  statusBadgeClassName?: string;
  showDirectionBadge?: boolean;
  directionBadgeColor?: string;
  sourceBadgeColor?: string;
  showOriginalAmount?: boolean;
  amountDisplay?: 'auto' | 'usd' | 'original';
  rowColorMode?: 'direction' | 'completedSign';
}

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  transactions,
  onEdit,
  onDelete,
  onCopy,
  showActions = false,
  hideDirection = false,
  hideProject = false,
  hideStatus = false,
  hideDescription = false,
  editOnlyPending = false,
  allowCopy = true,
  highlightPendingRow = false,
  pendingRowColor = '#ffffff',
  highlightPendingStatus = false,
  pendingStatusColor = '#111111',
  statusBadgeClassName,
  showDirectionBadge = false,
  directionBadgeColor = '#4b5563',
  sourceBadgeColor = '#6b7280',
  showOriginalAmount = false,
  amountDisplay = 'auto',
  rowColorMode = 'direction'
}) => {
  const { directions } = useTransactionDirections();
  const { types: transactionTypes } = useTransactionTypes();

  const directionMap = useMemo(() => {
    return directions.reduce<Record<number, string>>((accumulator, direction) => {
      accumulator[direction.id] = direction.title;
      return accumulator;
    }, {});
  }, [directions]);

  const transactionTypeMap = useMemo(() => {
    return transactionTypes.reduce<Record<number, string>>((accumulator, type) => {
      accumulator[type.id] = type.title;
      return accumulator;
    }, {});
  }, [transactionTypes]);

  const resolveDirectionTitle = (id?: number | null) => {
    if (id === undefined || id === null) {
      return 'Unknown';
    }
    return directionMap[id] ?? 'Unknown';
  };

  const hasDirectionValue = (id?: number | null) => id !== undefined && id !== null;

  const renderDirectionValue = (label: string, id: number | undefined | null, badgeColor: string) => {
    if (!hasDirectionValue(id)) {
      return null;
    }

    const title = resolveDirectionTitle(id);

    if (showDirectionBadge) {
      return (
        <span
          className="badge contact-type-badge"
          style={{ backgroundColor: badgeColor, color: '#ffffff' }}
        >
          {label}: {title}
        </span>
      );
    }

    return (
      <div>
        <span className="text-muted small">{label}: </span>
        {title}
      </div>
    );
  };

  const getTransactionTypeText = (typeId: number) => {
    return transactionTypeMap[typeId] ?? 'Unknown';
  };

  const isCompletedNegative = (transaction: Transaction): boolean =>
    transaction.isCompleted && transaction.totalAmount < 0;

  const getRowStyle = (transaction: Transaction): string => {
    if (rowColorMode === 'direction') {
      // Keep prop compatibility but apply unified color semantics.
    }
    if (!transaction.isCompleted) return 'table-pending';
    return isCompletedNegative(transaction) ? 'table-danger' : 'table-success';
  };

  const getRowInlineStyle = (transaction: Transaction): React.CSSProperties | undefined => {
    if (transaction.isCompleted) return undefined;
    return { backgroundColor: highlightPendingRow ? pendingRowColor : '#ffffff' };
  };

  return (
    <div className="table-responsive">
      <table className="table">
        <thead>
          <tr>
            <th className="text-start">Date</th>
            <th className="text-start">Title</th>
            <th className="text-start">Type</th>
            <th className="text-start">Amount</th>
            {!hideDirection && <th className="text-start">Source/Direction</th>}
            {!hideProject && <th className="text-start">Project</th>}
            {!hideStatus && <th className="text-start">Status</th>}
            {showActions && <th className="text-start">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {transactions.map((transaction) => (
            <tr
              key={transaction.id}
              className={getRowStyle(transaction)}
              style={getRowInlineStyle(transaction)}
            >
              <td>{formatDate(transaction.date)}</td>
              <td>
                <div>
                  <strong>
                    <Link
                      to={`/transactions?editId=${transaction.id}`}
                      className="text-decoration-none"
                      style={{ color: 'var(--primary)', fontWeight: 600 }}
                      title="Edit transaction"
                    >
                      {transaction.title}
                    </Link>
                  </strong>
                  {!hideDescription && transaction.description && (
                    <div className="text-muted small">{transaction.description}</div>
                  )}
                </div>
              </td>
              <td>{getTransactionTypeText(transaction.typeId)}</td>
              <td>
                {(() => {
                  if (amountDisplay === 'original') {
                    const originalAmount = transaction.amount ?? transaction.totalAmount;
                    return (
                      <strong>
                        {formatCurrency(originalAmount, transaction.currencyName)}
                      </strong>
                    );
                  }

                  const hasOriginal = transaction.amount !== transaction.totalAmount;
                  const shouldShowOriginal = showOriginalAmount && transaction.amount !== undefined && transaction.amount !== null
                    ? true
                    : hasOriginal;

                  const displayCurrency = amountDisplay === 'usd'
                    ? 'USD'
                    : (hasOriginal ? 'USD' : transaction.currencyName);

                  return (
                    <>
                      <strong>
                        {formatCurrency(transaction.totalAmount, displayCurrency)}
                      </strong>
                      {shouldShowOriginal && (
                        <div className="text-muted small">
                          {formatCurrency(transaction.amount, transaction.currencyName)}
                        </div>
                      )}
                    </>
                  );
                })()}
              </td>
              {!hideDirection && (
                <td>
                  <div className={showDirectionBadge ? 'd-flex flex-column align-items-start gap-1' : undefined}>
                    {renderDirectionValue('Source', transaction.sourceId, sourceBadgeColor)}
                    {renderDirectionValue('Direction', transaction.directionId, directionBadgeColor)}
                  </div>
                </td>
              )}
              {!hideProject && (
              <td>
                <Link
                  to={`/projects/${transaction.projectId}`}
                  className="text-decoration-underline"
                  style={{ color: 'var(--primary)', fontWeight: 600 }}
                >
                  <div>
                    <strong>{transaction.projectTitle}</strong>
                    <div className="text-muted small">{transaction.customerTitle}</div>
                  </div>
                </Link>
              </td>
              )}
              {!hideStatus && (
                <td>
                  <div>
                    {transaction.isCompleted ? (
                      <span
                        className="badge contact-type-badge"
                        style={{
                          backgroundColor: isCompletedNegative(transaction) ? '#dc2626' : '#16a34a',
                          color: '#ffffff'
                        }}
                      >
                        Completed
                      </span>
                    ) : (
                      <span
                        className={highlightPendingStatus ? (statusBadgeClassName ?? 'badge contact-type-badge') : undefined}
                        style={{
                          color: pendingStatusColor,
                          fontWeight: 600,
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb'
                        }}
                      >
                        Pending
                      </span>
                    )}
                    {transaction.isProcessed && (
                      <span
                        className="badge contact-type-badge ms-1"
                        style={{ backgroundColor: 'rgb(108, 117, 125)', color: '#ffffff' }}
                      >
                        Processed
                      </span>
                    )}
                  </div>
                </td>
              )}
              {showActions && (
                <td>
                  <div className="btn-group action-group" role="group">
                    {onEdit && (!editOnlyPending || !transaction.isCompleted) && (
                      <button
                        className="btn btn-sm action-btn action-edit"
                        onClick={() => onEdit(transaction)}
                        title="Edit"
                      >
                        <i className="fa-solid fa-pen" aria-hidden="true" />
                      </button>
                    )}
                    {onCopy && allowCopy && (
                      <button
                        className="btn btn-sm action-btn action-view"
                        onClick={() => onCopy(transaction)}
                        title="Copy"
                      >
                        <i className="fa-solid fa-copy" aria-hidden="true" />
                      </button>
                    )}
                    {onDelete && (
                      <button
                        className="btn btn-sm action-btn action-delete"
                        onClick={() => onDelete(transaction.id)}
                        title="Delete"
                      >
                        <i className="fa-solid fa-trash" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TransactionsTable;
